// D:\VEDITOR\VEDITOR\client\src\components\FeedbackDashboard.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, 
  faUser, 
  faCalendar, 
  faTrash, 
  faDownload,
  faLock,
  faEye,
  faEyeSlash,
  faRefresh,
  faFilter,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';
import './feedback.css';
import toast, { Toaster } from 'react-hot-toast'; // Import Toaster if you want to display toasts

const FeedbackDashboard = () => {
  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  // Dashboard states
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterRating, setFilterRating] = useState('all');

  // Check authentication on component mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('veditor_admin_auth');
    const authExpiry = localStorage.getItem('veditor_admin_expiry');
    
    // Check if authentication is still valid (24-hour expiry)
    if (savedAuth === 'true' && authExpiry && new Date().getTime() < parseInt(authExpiry)) {
      setIsAuthenticated(true);
      loadFeedback();
    } else {
      // Clear expired authentication
      localStorage.removeItem('veditor_admin_auth');
      localStorage.removeItem('veditor_admin_expiry');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password.trim()) {
      setError('Please enter admin password');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch('{process.env.REACT_APP_API_URL}/admin_auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: password }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        // Store authentication for 24 hours
        const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000);
        localStorage.setItem('veditor_admin_auth', 'true');
        localStorage.setItem('veditor_admin_expiry', expiryTime.toString());
        loadFeedback();
        toast.success('Admin access granted!');
      } else {
        setError(data.message || 'Access denied. Invalid credentials.');
        setPassword('');
        toast.error('Access denied');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Cannot connect to authentication server');
      toast.error('Authentication server error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('veditor_admin_auth');
    localStorage.removeItem('veditor_admin_expiry');
    setPassword('');
    setFeedbackList([]);
    toast.info('Logged out from admin dashboard');
  };

  const loadFeedback = () => {
    setLoading(true);
    
    try {
      // Try to load from localStorage first
      const storedFeedback = JSON.parse(localStorage.getItem('veditor_feedback')) || [];
      
      // Sort by date (newest first)
      const sortedFeedback = storedFeedback.sort((a, b) => {
        return new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date);
      });
      
      setFeedbackList(sortedFeedback);
      
      // Also try to load from backend API if available
      fetchBackendFeedback();
      
    } catch (error) {
      console.error('Error loading feedback:', error);
      toast.error('Failed to load feedback data');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const fetchBackendFeedback = async () => {
    try {
      const response = await fetch('{process.env.REACT_APP_API_URL}/save_feedback.php');
      if (response.ok) {
        const backendFeedback = await response.json();
        if (backendFeedback.success && backendFeedback.data) {
          // Merge with local feedback
          const localFeedback = JSON.parse(localStorage.getItem('veditor_feedback')) || [];
          const allFeedback = [...backendFeedback.data, ...localFeedback];
          
          // Remove duplicates based on timestamp and username
          const uniqueFeedback = Array.from(
            new Map(allFeedback.map(item => [
              `${item.username}-${item.timestamp || item.date}`,
              item
            ])).values()
          );
          
          setFeedbackList(uniqueFeedback.sort((a, b) => 
            new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date)
          ));
          
          toast.success('Loaded data from server');
        }
      }
    } catch (error) {
      console.log('Using local data only - server unavailable');
    }
  };

  const deleteFeedback = (index) => {
    if (window.confirm('Are you sure you want to delete this feedback?')) {
      const updatedFeedback = feedbackList.filter((_, i) => i !== index);
      localStorage.setItem('veditor_feedback', JSON.stringify(updatedFeedback));
      setFeedbackList(updatedFeedback);
      toast.success('Feedback deleted');
    }
  };

  const deleteAllFeedback = () => {
    if (window.confirm('Are you sure you want to delete ALL feedback? This cannot be undone.')) {
      localStorage.removeItem('veditor_feedback');
      setFeedbackList([]);
      toast.success('All feedback deleted');
    }
  };

  const exportToCSV = () => {
    if (feedbackList.length === 0) {
      toast.error('No feedback data to export!');
      return;
    }

    const headers = ['Name', 'Username', 'Room ID', 'Rating', 'Emotion', 'Feedback', 'Email', 'Position', 'Date', 'Time', 'IP'];
    const csvContent = [
      headers.join(','),
      ...feedbackList.map(f => [
        f.user_real_name || f.name || 'Anonymous',
        f.username,
        f.roomId,
        f.rating,
        f.emotion || '',
        `"${(f.feedback || '').replace(/"/g, '""')}"`,
        f.user_email || f.email || '',
        f.user_position || f.position || '',
        f.date || new Date(f.timestamp).toLocaleDateString() || 'Unknown',
        f.time || new Date(f.timestamp).toLocaleTimeString() || 'Unknown',
        f.ip || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `veditor_feedback_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${feedbackList.length} feedback entries`);
  };

  const filteredFeedback = filterRating === 'all' 
    ? feedbackList 
    : feedbackList.filter(f => f.rating === parseInt(filterRating));

  // Calculate statistics
  const averageRating = feedbackList.length > 0 
    ? (feedbackList.reduce((a, b) => a + b.rating, 0) / feedbackList.length).toFixed(1)
    : '0.0';

  const positiveRatings = feedbackList.filter(f => f.rating >= 4).length;
  const uniqueUsers = new Set(feedbackList.map(f => f.username || f.user_real_name || 'anonymous')).size;

  // --- LOGIN SCREEN ---
  if (!isAuthenticated) {
    return (
      <div className="login-screen">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <FontAwesomeIcon icon={faLock} size="3x" />
              <h1>Admin Dashboard</h1>
              <p>Restricted Access - Authentication Required</p>
            </div>
            
            <form onSubmit={handleLogin} className="login-form">
              <div className="input-group">
                <label htmlFor="password">Admin Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="login-input"
                    autoFocus
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <button type="submit" className="login-btn" disabled={loading}>
                <FontAwesomeIcon icon={faLock} /> 
                {loading ? 'Authenticating...' : 'Authenticate'}
              </button>
              
              <div className="admin-info">
                <p className="info-title">Secure Access:</p>
                <div className="info-item">
                  • Password is verified via secure server authentication
                </div>
                <div className="info-item">
                  • Session expires after 24 hours
                </div>
                <div className="info-item">
                  • Contact system administrator for password
                </div>
              </div>
            </form>
            
            <div className="login-footer">
              <small>
                <FontAwesomeIcon icon={faStar} /> This area is for authorized V Editor administrators only.
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }


  // --- DASHBOARD CONTENT ---
  return (
    <div className="dashboard-screen">
      <div className="dashboard-container">
        {/* Header with logout */}
        <div className="dashboard-header">
          <div className="header-left">
            <h1><FontAwesomeIcon icon={faStar} /> Feedback Dashboard</h1>
            <p className="subtitle">Admin Panel • Last updated: {new Date().toLocaleTimeString()}</p>
          </div>
          <div className="header-right">
            <button onClick={loadFeedback} className="refresh-btn-header" disabled={loading}>
              <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
            </button>
            <button onClick={handleLogout} className="logout-btn">
              <FontAwesomeIcon icon={faLock} /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="stat-content">
              <h3>{feedbackList.length}</h3>
              <p>Total Feedback</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faStar} />
            </div>
            <div className="stat-content">
              <h3>{averageRating}</h3>
              <p>Average Rating</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <span className="positive-icon">👍</span>
            </div>
            <div className="stat-content">
              <h3>{positiveRatings}</h3>
              <p>Positive (4-5 stars)</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="stat-content">
              <h3>{uniqueUsers}</h3>
              <p>Unique Users</p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="dashboard-controls">
          <div className="controls-left">
            <div className="filter-group">
              <label>
                <FontAwesomeIcon icon={faFilter} /> Filter by Rating:
              </label>
              <select 
                value={filterRating} 
                onChange={(e) => setFilterRating(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Ratings ({feedbackList.length})</option>
                <option value="5">★★★★★ (5 stars)</option>
                <option value="4">★★★★☆ (4 stars)</option>
                <option value="3">★★★☆☆ (3 stars)</option>
                <option value="2">★★☆☆☆ (2 stars)</option>
                <option value="1">★☆☆☆☆ (1 star)</option>
              </select>
            </div>
            
            <button 
              onClick={loadFeedback} 
              className="refresh-btn"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faRefresh} spin={loading} /> 
              {loading ? 'Loading...' : 'Refresh Data'}
            </button>
          </div>
          
          <div className="controls-right">
            <button 
              onClick={exportToCSV} 
              className="export-btn" 
              disabled={feedbackList.length === 0}
            >
              <FontAwesomeIcon icon={faFileExport} /> Export CSV ({feedbackList.length})
            </button>
            
            <button 
              onClick={deleteAllFeedback} 
              className="delete-all-btn"
              disabled={feedbackList.length === 0}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete All
            </button>
          </div>
        </div>

        {/* Feedback Table */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading feedback data...</p>
          </div>
        ) : filteredFeedback.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faStar} size="3x" />
            <h3>No feedback data available</h3>
            <p>
              {feedbackList.length === 0 
                ? 'No feedback has been collected yet. Try leaving a room and submitting feedback first.' 
                : 'No feedback matches your filter. Try changing the rating filter.'}
            </p>
            {filterRating !== 'all' && (
              <button onClick={() => setFilterRating('all')} className="reset-filter">
                Show All Feedback
              </button>
            )}
          </div>
        ) : (
          <div className="feedback-table-container">
            <div className="table-header">
              <div className="table-info">
                Showing {filteredFeedback.length} of {feedbackList.length} feedback entries
                {filterRating !== 'all' && ` (filtered: ${filterRating} stars)`}
              </div>
              <div className="table-actions">
                <button 
                  onClick={exportToCSV} 
                  className="export-small-btn"
                  disabled={filteredFeedback.length === 0}
                >
                  <FontAwesomeIcon icon={faDownload} /> Export Filtered ({filteredFeedback.length})
                </button>
              </div>
            </div>
            
            <div className="feedback-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Room</th>
                    <th>Rating</th>
                    <th>Emotion</th>
                    <th>Feedback</th>
                    <th>Position</th>
                    <th>Date & Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedback.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td className="name-cell">
                        <div className="user-avatar">
                          {(item.user_real_name || item.username || 'A').charAt(0).toUpperCase()}
                        </div>
                        <div className="name-details">
                          <div className="real-name">{item.user_real_name || item.name || 'Anonymous'}</div>
                          {item.user_email && (
                            <div className="user-email">{item.user_email}</div>
                          )}
                        </div>
                      </td>
                      <td className="username-cell">
                        <code>{item.username}</code>
                      </td>
                      <td className="room-cell">
                        <code>{item.roomId}</code>
                      </td>
                      <td className="rating-cell">
                        <div className="stars-container">
                          <div className="stars">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <FontAwesomeIcon 
                                key={i} 
                                icon={faStar} 
                                className={i < item.rating ? 'star-filled' : 'star-empty'}
                              />
                            ))}
                          </div>
                          <span className="rating-number">{item.rating}/5</span>
                        </div>
                      </td>
                      <td>
                        <span className={`emotion-badge ${item.emotion}`}>
                          {item.emotion || 'Not specified'}
                          {item.emotion === 'happy' && ' 😊'}
                          {item.emotion === 'neutral' && ' 😐'}
                          {item.emotion === 'sad' && ' 😔'}
                        </span>
                      </td>
                      <td className="feedback-cell">
                        <div className="feedback-text">
                          {item.feedback ? (
                            <>
                              <div className="feedback-preview">
                                {item.feedback.length > 100 
                                  ? `${item.feedback.substring(0, 100)}...` 
                                  : item.feedback}
                              </div>
                              {item.feedback.length > 100 && (
                                <button 
                                  className="view-full-btn"
                                  onClick={() => alert(`Full Feedback:\n\n${item.feedback}`)}
                                >
                                  View Full
                                </button>
                              )}
                            </>
                          ) : (
                            <em className="no-comment">No comment provided</em>
                          )}
                        </div>
                      </td>
                      <td className="position-cell">
                        <span className="position-badge">
                          {item.user_position || item.position || 'Not specified'}
                        </span>
                      </td>
                      <td className="date-cell">
                        <div className="date-content">
                          <FontAwesomeIcon icon={faCalendar} />
                          <div>
                            <div className="date">{item.date || new Date(item.timestamp).toLocaleDateString() || 'Unknown'}</div>
                            <div className="time">{item.time || new Date(item.timestamp).toLocaleTimeString() || 'Unknown'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button 
                          onClick={() => deleteFeedback(index)}
                          className="delete-btn"
                          title="Delete this feedback"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                        <button 
                          className="copy-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                            toast.success('Feedback copied to clipboard');
                          }}
                          title="Copy feedback data"
                        >
                          📋
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination/Footer */}
            <div className="table-footer">
              <div className="footer-info">
                Data loaded from: {localStorage.getItem('veditor_feedback') ? 'LocalStorage' : 'No data'} 
                {feedbackList.length > 0 && ` • Last entry: ${new Date(feedbackList[0].timestamp || feedbackList[0].date).toLocaleString()}`}
              </div>
              <div className="footer-actions">
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="scroll-top-btn"
                >
                  ↑ Back to Top
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDashboard;